/**
 * Utility functions for custom flag management and conflict resolution
 */

/**
 * Calculate text color based on background color brightness
 * Returns black or white for optimal contrast
 */
export const calculateTextColor = (backgroundColor) => {
  if (!backgroundColor) return '#000000';
  
  // Remove # if present
  let hex = backgroundColor.replace('#', '');
  if (hex.length === 3) {
    hex = hex.split('').map(char => char + char).join('');
  }
  
  // Parse RGB values
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  if ([r, g, b].some(value => Number.isNaN(value))) {
    return '#000000';
  }
  
  // Calculate brightness using relative luminance formula
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  
  // Return black for light backgrounds, white for dark backgrounds
  return brightness > 128 ? '#000000' : '#ffffff';
};

export const blendHexWithWhite = (backgroundColor, opacity = 0.15) => {
  if (!backgroundColor) return '#ffffff';

  let hex = backgroundColor.replace('#', '');
  if (hex.length === 3) {
    hex = hex.split('').map(char => char + char).join('');
  }

  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  if ([r, g, b].some(value => Number.isNaN(value))) {
    return '#ffffff';
  }

  const blend = value => Math.round((value * opacity) + (255 * (1 - opacity)));
  const toHex = value => value.toString(16).padStart(2, '0');

  return `#${toHex(blend(r))}${toHex(blend(g))}${toHex(blend(b))}`;
};

export const calculateTintedFlagTextColor = (backgroundColor, opacity = 0.15) => {
  return calculateTextColor(blendHexWithWhite(backgroundColor, opacity));
};

// Automatic indicators that could conflict with custom flags
const AUTOMATIC_INDICATORS = {
  'Flip': 'isFlip',
  'Late Checkout': 'isLateCheckout', 
  'Early Checkin': 'isEarlyCheckin',
  'Owner Arrival': 'isOwnerArrival',
  'Owner Departure': 'isOwnerDeparture',
  'Maintenance Start': 'isMaintenanceStart',
  'Maintenance Ending': 'isMaintenanceEnding',
  'Date Override': 'hasDateOverride',
  'Time Override': 'hasTimeOverride'
};

// Flag templates with suggested colors - includes both system indicators and custom flags
export const FLAG_TEMPLATES = {
  system: {
    values: ['Flip', 'Late Checkout', 'Early Check-in', 'Date Changed', 'Owner Arrival', 'Owner Departure', 'Maintenance Start', 'Maintenance Ending', 'Ready for Inspection'],
    defaultColor: '#3b82f6', // Blue
    icon: 'system'
  },
  priority: {
    values: ['High Priority', 'Urgent', 'Low Priority', 'Critical'],
    defaultColor: '#ef4444', // Red
    icon: 'priority'
  },
  status: {
    values: ['Follow-up Required', 'Needs Attention', 'In Review', 'Quality Check'],
    defaultColor: '#3b82f6', // Blue
    icon: 'status'
  },
  category: {
    values: ['VIP Guest', 'Special Request', 'Equipment Needed', 'Pet Friendly', 'Accessibility'],
    defaultColor: '#22c55e', // Green
    icon: 'category'
  },
  maintenance: {
    values: ['Deep Clean', 'Repair Needed', 'Inspection Required', 'Safety Check'],
    defaultColor: '#f97316', // Orange
    icon: 'maintenance'
  },
  guest: {
    values: ['First Time Guest', 'Returning Guest', 'Group Booking', 'Extended Stay'],
    defaultColor: '#a855f7', // Purple
    icon: 'guest'
  }
};

// Predefined colors for flags
export const FLAG_COLORS = [
  { name: 'Red', value: '#ef4444', bg: 'bg-red-100', text: 'text-red-800' },
  { name: 'Orange', value: '#f97316', bg: 'bg-orange-100', text: 'text-orange-800' },
  { name: 'Yellow', value: '#eab308', bg: 'bg-yellow-100', text: 'text-yellow-800' },
  { name: 'Green', value: '#22c55e', bg: 'bg-green-100', text: 'text-green-800' },
  { name: 'Blue', value: '#3b82f6', bg: 'bg-blue-100', text: 'text-blue-800' },
  { name: 'Purple', value: '#a855f7', bg: 'bg-purple-100', text: 'text-purple-800' },
  { name: 'Pink', value: '#ec4899', bg: 'bg-pink-100', text: 'text-pink-800' },
  { name: 'Gray', value: '#6b7280', bg: 'bg-gray-100', text: 'text-gray-800' }
];

/**
 * Detect potential conflicts between custom flags and automatic indicators
 * @param {Object} customFlag - The custom flag to check
 * @param {Object} job - The job object
 * @returns {Array} Array of conflict warnings
 */
export const detectFlagConflicts = (customFlag, job) => {
  const conflicts = [];
  
  // Check for naming conflicts with automatic indicators
  Object.entries(AUTOMATIC_INDICATORS).forEach(([indicatorName, jobProperty]) => {
    if (customFlag.flagValue.toLowerCase().includes(indicatorName.toLowerCase()) ||
        indicatorName.toLowerCase().includes(customFlag.flagValue.toLowerCase())) {
      conflicts.push({
        type: 'naming_conflict',
        message: `Flag "${customFlag.flagValue}" may conflict with automatic "${indicatorName}" indicator`,
        severity: 'warning'
      });
    }
  });

  // Check for semantic conflicts based on job status
  if (customFlag.flagType === 'status' && customFlag.flagValue.toLowerCase().includes('urgent') && job.status === 'CANCELLED') {
    conflicts.push({
      type: 'semantic_conflict',
      message: 'Urgent status flag conflicts with cancelled job status',
      severity: 'error'
    });
  }

  if (customFlag.flagType === 'priority' && customFlag.flagValue.toLowerCase().includes('low') && job.status === 'IN_PROGRESS') {
    conflicts.push({
      type: 'semantic_conflict',
      message: 'Low priority flag may conflict with in-progress job',
      severity: 'warning'
    });
  }

  // Check for duplicate flags
  if (job.customFlags) {
    const duplicateFlag = job.customFlags.find(flag => 
      flag.id !== customFlag.id && 
      flag.flagValue.toLowerCase() === customFlag.flagValue.toLowerCase() &&
      flag.flagType === customFlag.flagType
    );
    
    if (duplicateFlag) {
      conflicts.push({
        type: 'duplicate',
        message: `Duplicate flag "${customFlag.flagValue}" of type "${customFlag.flagType}" already exists`,
        severity: 'error'
      });
    }
  }

  return conflicts;
};

/**
 * Validate a custom flag before adding it
 * @param {Object} flag - The flag to validate
 * @param {Object} job - The job object
 * @returns {Object} Validation result with isValid and errors
 */
export const validateCustomFlag = (flag, job) => {
  const errors = [];
  const warnings = [];

  // Required field validation
  if (!flag.flagValue || flag.flagValue.trim().length === 0) {
    errors.push('Flag value is required');
  }

  if (!flag.flagType || flag.flagType.trim().length === 0) {
    errors.push('Flag type is required');
  }

  // Length validation
  if (flag.flagValue && flag.flagValue.length > 50) {
    errors.push('Flag value must be 50 characters or less');
  }

  if (flag.flagType && flag.flagType.length > 20) {
    errors.push('Flag type must be 20 characters or less');
  }

  // Character validation
  if (flag.flagValue && !/^[a-zA-Z0-9\s\-_]+$/.test(flag.flagValue)) {
    warnings.push('Flag value contains special characters. Consider using only letters, numbers, spaces, hyphens, and underscores.');
  }

  // Color validation
  if (flag.flagColor && !/^#[0-9A-F]{6}$/i.test(flag.flagColor)) {
    errors.push('Invalid flag color format. Use hex color format (e.g., #FF0000)');
  }

  // Check for conflicts
  const conflicts = detectFlagConflicts(flag, job);
  conflicts.forEach(conflict => {
    if (conflict.severity === 'error') {
      errors.push(conflict.message);
    } else {
      warnings.push(conflict.message);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Get suggested flag color based on type and value
 * @param {string} flagType - The type of flag
 * @param {string} flagValue - The value of the flag
 * @returns {string} Suggested hex color
 */
export const getSuggestedFlagColor = (flagType, flagValue) => {
  // Check if template has a default color
  if (FLAG_TEMPLATES[flagType]) {
    return FLAG_TEMPLATES[flagType].defaultColor;
  }

  // Color suggestions based on flag value keywords
  const value = flagValue.toLowerCase();
  
  if (value.includes('urgent') || value.includes('critical') || value.includes('high priority')) {
    return '#ef4444'; // Red
  }
  
  if (value.includes('low') || value.includes('completed') || value.includes('done')) {
    return '#22c55e'; // Green
  }
  
  if (value.includes('maintenance') || value.includes('repair') || value.includes('inspection')) {
    return '#f97316'; // Orange
  }
  
  if (value.includes('vip') || value.includes('special') || value.includes('important')) {
    return '#a855f7'; // Purple
  }
  
  if (value.includes('guest') || value.includes('booking') || value.includes('reservation')) {
    return '#3b82f6'; // Blue
  }

  // Default color
  return '#6b7280'; // Gray
};

/**
 * Sort custom flags by priority and type
 * @param {Array} flags - Array of custom flags
 * @returns {Array} Sorted array of flags
 */
export const sortCustomFlags = (flags) => {
  const typePriority = {
    'priority': 1,
    'status': 2,
    'category': 3,
    'maintenance': 4,
    'guest': 5,
    'custom': 6
  };

  return [...flags].sort((a, b) => {
    // First sort by type priority
    const aPriority = typePriority[a.flagType] || 999;
    const bPriority = typePriority[b.flagType] || 999;
    
    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }
    
    // Then by sort order
    if (a.sortOrder !== b.sortOrder) {
      return a.sortOrder - b.sortOrder;
    }
    
    // Finally by creation date
    return new Date(a.createdAt) - new Date(b.createdAt);
  });
};

/**
 * Get flag display properties for consistent styling
 * @param {Object} flag - The custom flag
 * @returns {Object} Display properties
 */
export const getFlagDisplayProperties = (flag) => {
  const color = flag.flagColor || getSuggestedFlagColor(flag.flagType, flag.flagValue);
  
  // Calculate text color based on background brightness
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  const textColor = brightness > 128 ? '#000000' : '#ffffff';
  
  return {
    backgroundColor: `${color}20`,
    color: textColor,
    borderColor: `${color}40`,
    icon: FLAG_TEMPLATES[flag.flagType]?.icon || 'default'
  };
};

/**
 * Check if a flag should be shown based on job context
 * @param {Object} flag - The custom flag
 * @param {Object} job - The job object
 * @returns {boolean} Whether the flag should be displayed
 */
export const shouldShowFlag = (flag, job) => {
  // Don't show flags for deleted jobs
  if (job.isDeleted) {
    return false;
  }
  
  // Don't show certain flags for cancelled jobs
  if (job.status === 'CANCELLED' && flag.flagType === 'priority' && flag.flagValue.toLowerCase().includes('urgent')) {
    return false;
  }
  
  return true;
};
